import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const ContentManagement = () => {
  const theme = useThemeClasses();
  const [pages, setPages] = useState([]);
  const [filteredPages, setFilteredPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPages, setSelectedPages] = useState([]);
  const [formData, setFormData] = useState({
    page_type: 'custom',
    title: '',
    slug: '',
    content: '',
    is_published: true,
    meta_title: '',
    meta_description: ''
  });

  const templates = {
    about: {
      title: 'About Us',
      content: `<div style="max-width: 800px; margin: 0 auto; padding: 20px;">
  <h1>About RG-3D</h1>
  <p>Welcome to RG-3D, your premier destination for professional 3D printing and laser engraving services.</p>
  
  <h2>Our Story</h2>
  <p>Founded in [YEAR], we've been helping customers bring their creative visions to life through cutting-edge 3D printing technology.</p>
  
  <h2>What We Offer</h2>
  <ul>
    <li>Professional 3D Printing Services</li>
    <li>Custom Laser Engraving</li>
    <li>Over 350+ Material Options</li>
    <li>Fast Turnaround Times</li>
    <li>Expert Design Consultation</li>
  </ul>
  
  <h2>Why Choose Us</h2>
  <p>We combine state-of-the-art technology with personalized service to deliver exceptional results every time.</p>
</div>`
    },
    terms: {
      title: 'Terms of Service',
      content: `<div style="max-width: 800px; margin: 0 auto; padding: 20px;">
  <h1>Terms of Service</h1>
  <p><em>Last Updated: ${new Date().toLocaleDateString()}</em></p>
  
  <h2>1. Acceptance of Terms</h2>
  <p>By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.</p>
  
  <h2>2. Use License</h2>
  <p>Permission is granted to temporarily download one copy of the materials on our website for personal, non-commercial transitory viewing only.</p>
  
  <h2>3. Disclaimer</h2>
  <p>The materials on our website are provided on an 'as is' basis. We make no warranties, expressed or implied.</p>
  
  <h2>4. Limitations</h2>
  <p>In no event shall RG-3D or its suppliers be liable for any damages arising out of the use or inability to use our services.</p>
  
  <h2>5. Contact Us</h2>
  <p>If you have any questions about these Terms, please contact us at: support@rg3d.ca</p>
</div>`
    },
    privacy: {
      title: 'Privacy Policy',
      content: `<div style="max-width: 800px; margin: 0 auto; padding: 20px;">
  <h1>Privacy Policy</h1>
  <p><em>Last Updated: ${new Date().toLocaleDateString()}</em></p>
  
  <h2>1. Information We Collect</h2>
  <p>We collect information that you provide directly to us, including name, email, billing and shipping addresses, payment information, and order history.</p>
  
  <h2>2. How We Use Your Information</h2>
  <p>We use the information we collect to process and fulfill orders, communicate with you about your orders, send marketing communications (with consent), and improve our services.</p>
  
  <h2>3. Information Sharing</h2>
  <p>We do not sell or rent your personal information to third parties. We may share your information with service providers who assist us in operating our business.</p>
  
  <h2>4. Data Security</h2>
  <p>We implement appropriate security measures to protect your personal information from unauthorized access, alteration, or disclosure.</p>
  
  <h2>5. Contact Us</h2>
  <p>For privacy-related questions, email us at: privacy@rg3d.ca</p>
</div>`
    },
    faq: {
      title: 'FAQ',
      content: `<div style="max-width: 800px; margin: 0 auto; padding: 20px;">
  <h1>Frequently Asked Questions</h1>
  
  <h2>Ordering & Pricing</h2>
  <details>
    <summary><strong>How do I place an order?</strong></summary>
    <p>Browse our products, select the item you want, customize it if needed, and add it to your cart. Follow the checkout process to complete your order.</p>
  </details>
  
  <details>
    <summary><strong>What payment methods do you accept?</strong></summary>
    <p>We accept credit cards, PayPal, and e-Transfer for Canadian customers.</p>
  </details>
  
  <h2>Shipping & Delivery</h2>
  <details>
    <summary><strong>How long does shipping take?</strong></summary>
    <p>Standard shipping typically takes 5-7 business days. Express options are available.</p>
  </details>
</div>`
    }
  };

  useEffect(() => {
    loadPages();
    // Add keyboard shortcut for save
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && showForm) {
        e.preventDefault();
        document.getElementById('save-page-btn')?.click();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showForm]);

  useEffect(() => {
    filterPages();
  }, [pages, searchQuery, filterType, filterStatus]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/content-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const filterPages = () => {
    let filtered = [...pages];
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(page => 
        page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(page => page.page_type === filterType);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      const isPublished = filterStatus === 'published';
      filtered = filtered.filter(page => page.is_published === isPublished);
    }
    
    setFilteredPages(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingPage) {
        await axios.put(`${API}/api/admin/content-pages/${editingPage.id}`, formData, { headers });
        toast.success('Page updated successfully!');
      } else {
        await axios.post(`${API}/api/admin/content-pages`, formData, { headers });
        toast.success('Page created successfully!');
      }
      
      setShowForm(false);
      setEditingPage(null);
      setFormData({
        page_type: 'custom',
        title: '',
        slug: '',
        content: '',
        is_published: true,
        meta_title: '',
        meta_description: ''
      });
      loadPages();
    } catch (error) {
      console.error('Failed to save page:', error);
      toast.error('Failed to save page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      page_type: page.page_type,
      title: page.title,
      slug: page.slug,
      content: page.content,
      is_published: page.is_published,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || ''
    });
    setShowForm(true);
    setPreviewMode(false);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/content-pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page deleted successfully!');
      loadPages();
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPages.length === 0) {
      toast.error('Please select pages to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedPages.length} page(s)?`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await Promise.all(
        selectedPages.map(id => 
          axios.delete(`${API}/api/admin/content-pages/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedPages.length} page(s) deleted successfully!`);
      setSelectedPages([]);
      loadPages();
    } catch (error) {
      console.error('Failed to delete pages:', error);
      toast.error('Failed to delete some pages');
    }
  };

  const handleBulkPublish = async (publish) => {
    if (selectedPages.length === 0) {
      toast.error('Please select pages');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await Promise.all(
        selectedPages.map(id => {
          const page = pages.find(p => p.id === id);
          return axios.put(`${API}/api/admin/content-pages/${id}`, 
            { ...page, is_published: publish }, 
            { headers }
          );
        })
      );
      toast.success(`${selectedPages.length} page(s) ${publish ? 'published' : 'unpublished'} successfully!`);
      setSelectedPages([]);
      loadPages();
    } catch (error) {
      console.error('Failed to update pages:', error);
      toast.error('Failed to update some pages');
    }
  };

  const togglePageSelection = (pageId) => {
    setSelectedPages(prev => 
      prev.includes(pageId) 
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPages.length === filteredPages.length) {
      setSelectedPages([]);
    } else {
      setSelectedPages(filteredPages.map(p => p.id));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const loadTemplate = (type) => {
    if (templates[type]) {
      setFormData({
        ...formData,
        page_type: type,
        title: templates[type].title,
        slug: type === 'custom' ? '' : type,
        content: templates[type].content
      });
      toast.success('Template loaded!');
    }
  };

  const insertFormatting = (tag) => {
    const textarea = document.getElementById('content-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    let newText = '';
    switch(tag) {
      case 'h2':
        newText = `<h2>${selectedText || 'Heading'}</h2>`;
        break;
      case 'p':
        newText = `<p>${selectedText || 'Paragraph text'}</p>`;
        break;
      case 'b':
        newText = `<strong>${selectedText || 'bold text'}</strong>`;
        break;
      case 'i':
        newText = `<em>${selectedText || 'italic text'}</em>`;
        break;
      case 'ul':
        newText = `<ul>\n  <li>${selectedText || 'List item'}</li>\n</ul>`;
        break;
      case 'link':
        newText = `<a href="https://example.com">${selectedText || 'link text'}</a>`;
        break;
      default:
        return;
    }
    
    const newContent = formData.content.substring(0, start) + newText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
  };

  const stats = {
    total: pages.length,
    published: pages.filter(p => p.is_published).length,
    drafts: pages.filter(p => !p.is_published).length,
    types: [...new Set(pages.map(p => p.page_type))].length
  };

  if (loading) return <div className="p-8">Loading pages...</div>;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>📄 Content Pages</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>Create and manage static pages</p>
          
          {/* Quick Stats */}
          <div className="flex gap-4 mt-4">
            <div className={`${theme.card} px-4 py-2 rounded-lg border-l-4 border-blue-500`}>
              <p className={`text-xs ${theme.textSecondary} font-medium`}>Total Pages</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
            <div className={`${theme.card} px-4 py-2 rounded-lg border-l-4 border-green-500`}>
              <p className={`text-xs ${theme.textSecondary} font-medium`}>Published</p>
              <p className="text-2xl font-bold text-green-600">{stats.published}</p>
            </div>
            <div className={`${theme.card} px-4 py-2 rounded-lg border-l-4 border-gray-500`}>
              <p className={`text-xs ${theme.textSecondary} font-medium`}>Drafts</p>
              <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats.drafts}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingPage(null);
            setPreviewMode(false);
            setFormData({
              page_type: 'custom',
              title: '',
              slug: '',
              content: '',
              is_published: true,
              meta_title: '',
              meta_description: ''
            });
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showForm ? '✕ Cancel' : '+ Create Page'}
        </button>
      </div>

      {/* Search and Filters */}
      {!showForm && (
        <div className={`${theme.card} p-4 rounded-lg shadow-sm border`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <input
                type="text"
                placeholder="🔍 Search pages by title, slug, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
            >
              <option value="all">All Types</option>
              <option value="about">About</option>
              <option value="terms">Terms</option>
              <option value="privacy">Privacy</option>
              <option value="faq">FAQ</option>
              <option value="custom">Custom</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          
          {/* Bulk Actions */}
          {selectedPages.length > 0 && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-900">
                {selectedPages.length} page(s) selected
              </span>
              <button
                onClick={() => handleBulkPublish(true)}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                ✓ Publish
              </button>
              <button
                onClick={() => handleBulkPublish(false)}
                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
              >
                ○ Unpublish
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              >
                🗑️ Delete
              </button>
              <button
                onClick={() => setSelectedPages([])}
                className={`px-3 py-1 bg-gray-300 ${theme.textPrimary} rounded text-sm hover:bg-gray-400`}
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Form - KEEPING EXISTING FORM CODE */}
      {showForm && (
        <div className={`${theme.card} rounded-lg shadow-sm border ${theme.border}`}>
          <div className={`p-6 border-b ${theme.border}`}>
            <h3 className="text-lg font-semibold">
              {editingPage ? 'Edit Page' : 'Create New Page'}
            </h3>
            <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Tip: Press Ctrl+S to save</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Template Selector */}
            {!editingPage && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium mb-3">📋 Start with a template:</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(templates).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => loadTemplate(type)}
                      className={`px-4 py-2 ${theme.card} border border-blue-300 rounded-lg hover:bg-blue-50 text-sm capitalize transition-colors`}
                    >
                      {templates[type].title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Rest of form remains the same as before */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Page Type</label>
                <select
                  value={formData.page_type}
                  onChange={(e) => setFormData({ ...formData, page_type: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="about">About Us</option>
                  <option value="terms">Terms of Service</option>
                  <option value="privacy">Privacy Policy</option>
                  <option value="faq">FAQ</option>
                  <option value="custom">Custom Page</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Page Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="About Our Company"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">URL Slug *</label>
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${window.location.origin}/pages/${formData.slug}`, `URL`)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  📋 Copy URL
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className={`${theme.textSecondary}`}>/pages/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, `-`) })}
                  required
                  className={`flex-1 px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="about-us"
                />
              </div>
              <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 URL will be: {window.location.origin}/pages/{formData.slug || 'your-slug'}</p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Page Content</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewMode(!previewMode)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      previewMode ? 'bg-blue-600 text-white' : `bg-gray-200 ${theme.textPrimary} hover:bg-gray-300`
                    }`}
                  >
                    {previewMode ? '📝 Edit' : '👁️ Preview'}
                  </button>
                </div>
              </div>

              {!previewMode && (
                <>
                  <div className={`flex flex-wrap gap-1 mb-2 p-2 ${theme.bgSecondary} rounded-lg border ${theme.border}`}>
                    <button type="button" onClick={() => insertFormatting(`h2`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-semibold`} title="Heading">H2</button>
                    <button type="button" onClick={() => insertFormatting(`p`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm`} title="Paragraph">P</button>
                    <button type="button" onClick={() => insertFormatting(`b`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-bold`} title="Bold">B</button>
                    <button type="button" onClick={() => insertFormatting(`i`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm italic`} title="Italic">I</button>
                    <button type="button" onClick={() => insertFormatting(`ul`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm`} title="List">• List</button>
                    <button type="button" onClick={() => insertFormatting(`link`)} className={`px-3 py-1 ${theme.card} border rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm`} title="Link">🔗 Link</button>
                  </div>
                  <textarea
                    id="content-editor"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows="15"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="<h1>Your content here</h1>"
                  />
                </>
              )}

              {previewMode && (
                <div className={`border ${theme.border} rounded-lg p-6 ${theme.card} min-h-[400px]`}>
                  <div dangerouslySetInnerHTML={{ __html: formData.content || `<p class="text-gray-400">No content to preview</p>` }} />
                </div>
              )}
            </div>

            <div className={`border-t ${theme.border} pt-6`}>
              <h4 className={`font-medium mb-4 ${theme.textPrimary}`}>SEO Settings (Optional)</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Meta Title</label>
                  <input
                    type="text"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Leave empty to use page title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Meta Description</label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    rows="2"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Brief description for search engines"
                  />
                </div>
              </div>
            </div>

            <div className={`flex items-center gap-3 p-4 ${theme.bgSecondary} rounded-lg`}>
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="is_published" className="text-sm font-medium">
                Publish this page immediately
              </label>
            </div>

            <div className={`flex gap-3 pt-4 border-t ${theme.border}`}>
              <button
                id="save-page-btn"
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {editingPage ? '💾 Update Page' : '✓ Create Page'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingPage(null);
                  setPreviewMode(false);
                }}
                className={`px-6 py-2 bg-gray-200 ${theme.textPrimary} rounded-lg hover:bg-gray-300 transition-colors`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pages List */}
      {!showForm && (
        <div className={`${theme.card} rounded-lg shadow-sm border overflow-hidden`}>
          <table className={`min-w-full divide-y ${theme.tableBorder}`}>
            <thead className={theme.tableHeader}>
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedPages.length === filteredPages.length && filteredPages.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Title</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Type</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>URL</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
              </tr>
            </thead>
            <tbody className={theme.tableBorder}>
              {filteredPages.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-400">
                      <p className="text-lg mb-2">📄 {searchQuery || filterType !== 'all' || filterStatus !== 'all' ? 'No pages match your filters' : 'No pages created yet'}</p>
                      <p className="text-sm">{searchQuery || filterType !== 'all' || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'Click "Create Page" to add your first content page'}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPages.map((page) => (
                  <tr key={page.id} className={theme.tableRow}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPages.includes(page.id)}
                        onChange={() => togglePageSelection(page.id)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${theme.textPrimary}`}>{page.title}</div>
                      {page.meta_description && (
                        <div className={`text-xs ${theme.textMuted} mt-1`}>{page.meta_description.substring(0, 60)}...</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-lg bg-purple-100 text-purple-800 capitalize">
                        {page.page_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <a 
                        href={`/pages/${page.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        /pages/{page.slug} ↗
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-lg font-medium ${
                        page.is_published 
                          ? 'bg-green-100 text-green-800' 
                          : `bg-gray-200 ${theme.textPrimary}`
                      }`}>
                        {page.is_published ? '✓ Published' : '○ Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleEdit(page)}
                        className="text-blue-600 hover:text-blue-800 font-medium mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(page.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};